import { useState, useEffect } from "react";

import Form from "V2.0/components/Form";
import { getAgents } from "../../../Assistants/service";

import {
  Dialog,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "V2.0/common/dialog";
import Joi from "joi-browser";
import httpService from "V2.0/services/httpService";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";

export const AssignmentMode = {
  ASSIGN: "ASSIGN",
  RE_ASSIGN: "RE_ASSIGN",
};

const AgentAssignComponent = {
  [AssignmentMode.ASSIGN]: (props) => <AgentAssignment {...props} />,
  [AssignmentMode.RE_ASSIGN]: (props) => <AgentReassignment {...props} />,
};

const defaultOption = [{ id: 0, label: "(No agent selected)" }];

const getTemplate = (humanAgents) => [
  {
    name: "humanAgentId",
    label: "Human Agent",
    description: "Select the human agent to which you want to reassign to",
    type: "select",
    options: defaultOption.concat(
      humanAgents.map((agent) => ({
        id: agent._id,
        value: agent._id,
        label: `${agent.name} (${agent.email})`,
      }))
    ),
  },
];

const schema = {
  humanAgentId: Joi.string().required().label("Human Agent"),
};

function useWhatsAppAgents(projectId, assistantId) {
  const [whatsappAgents, setWhatsappAgents] = useState([]);

  const fetchHumanAgents = async (projectId) => {
    try {
      const response = await getAgents({ workspaceId: projectId, assistantId });
      setWhatsappAgents(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchHumanAgents(projectId);
    }
  }, [projectId]);

  const changeAssignment = (data, projectId, conversationId) => {
    return httpService.patch(
      `/conversation/whitelabelChangeHumanAgentAssignment/${projectId}`,
      JSON.stringify({
        conversationId,
        ...data,
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  return {
    whatsappAgents,
    fetchWhatsappAgents: fetchHumanAgents,
    changeAssignment,
  };
}

export function AgentAssign(props) {
  const { mode, ...restProps } = props;
  return AgentAssignComponent[mode]({ ...restProps });
}

function AgentAssignment(props) {
  const {
    isOpen,
    onClose,
    projectId,
    conversationId,
    fetchConversations,
    agentId,
    resetSelectedConversation,
    assistantId,
  } = props;

  const { whatsappAgents, changeAssignment } = useWhatsAppAgents(
    projectId,
    assistantId
  );

  const onSubmit = async (data) => {
    try {
      message.loading("Assigning agent");
      const response = await changeAssignment(data, projectId, conversationId);
      if (response.status === 200) {
        resetSelectedConversation();
        fetchConversations(1);
        message.success("Agent Assigned");
        onClose();
      }
    } catch (error) {
      console.error(error);
      showErrorToast(error, "Failed to Assisgn Agent");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Assign Human Agent</DialogTitle>
      <DialogBody>
        <AgentAssignmentForm
          agents={whatsappAgents}
          onSubmit={onSubmit}
          buttons={{
            buttons: "2",
            primaryLabel: "Assign",
            secondaryLabel: "Cancel",
          }}
          onClose={onClose}
        />
      </DialogBody>
    </Dialog>
  );
}

function AgentReassignment(props) {
  const {
    isOpen,
    onClose,
    projectId,
    conversationId,
    fetchConversations,
    agentId,
    resetSelectedConversation,
    assistantId,
  } = props;
  const { whatsappAgents, changeAssignment } = useWhatsAppAgents(
    projectId,
    assistantId
  );

  const agentsExcludingActiveAgent = whatsappAgents.filter(
    (agent) => agent._id !== agentId
  );

  const onSubmit = async (data) => {
    try {
      message.loading("Reassigning agent");
      const response = await changeAssignment(data, projectId, conversationId);
      if (response.status === 200) {
        resetSelectedConversation();
        fetchConversations(1);
        message.success("Reassigned");
        onClose();
      }
    } catch (error) {
      showErrorToast(error, "Failed to Reassign");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Reassign Human Agent</DialogTitle>
      <DialogBody>
        <AgentAssignmentForm
          agents={agentsExcludingActiveAgent}
          onSubmit={onSubmit}
          buttons={{
            buttons: "2",
            primaryLabel: "Reassign",
            secondaryLabel: "Cancel",
          }}
          onClose={onClose}
        />
      </DialogBody>
    </Dialog>
  );
}

function AgentAssignmentForm(props) {
  const { agents, onSubmit, buttons, onClose } = props;
  const [loading, setLoading] = useState(false);

  const template = getTemplate(agents);

  return (
    <Form
      template={template}
      schema={schema}
      inputField={{ humanAgentId: "" }}
      buttonInfo={buttons}
      onSubmit={onSubmit}
      loading={loading}
      setLoading={setLoading}
      handleSecondary={onClose}
    />
  );
}
